import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { emailAPI, foldersAPI, signaturesAPI } from '../services/api';
import { useOffline } from '../context/OfflineContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Inbox,
  Send,
  FileText,
  Trash2,
  AlertCircle,
  Folder,
  Plus,
  Search,
  RefreshCw,
  Star,
  Mail,
  MailOpen,
  Paperclip,
  MoreVertical,
  ChevronRight,
  Pencil,
  Archive,
  Reply,
  Forward,
  X,
  GripVertical,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import ComposeEmail from '../components/email/ComposeEmail';
import EmailDetail from '../components/email/EmailDetail';
import AdvancedSearch from '../components/email/AdvancedSearch';

const folderIcons = {
  inbox: Inbox,
  sent: Send,
  drafts: FileText,
  trash: Trash2,
  spam: AlertCircle,
  custom: Folder,
};

export default function EmailPage() {
  const { folder: urlFolder } = useParams();
  const navigate = useNavigate();
  const { isOnline, cacheEmails, getCachedEmails, queueEmail } = useOffline();
  const [folders, setFolders] = useState([]);
  const [emails, setEmails] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(urlFolder || 'INBOX');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);
  const [signatures, setSignatures] = useState([]);
  const [emailStats, setEmailStats] = useState({ total: 0, unread: 0 });
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null);

  const handleDragStart = (e, emailId) => {
    e.dataTransfer.setData('text/plain', emailId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, folderName) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolder(folderName);
  };

  const handleDragLeave = () => {
    setDragOverFolder(null);
  };

  const handleDrop = async (e, targetFolder) => {
    e.preventDefault();
    setDragOverFolder(null);
    const emailId = e.dataTransfer.getData('text/plain');
    if (emailId) {
      await handleMoveEmail(emailId, targetFolder);
    }
  };

  const loadFolders = useCallback(async () => {
    try {
      const response = await foldersAPI.getFolders();
      setFolders(response.data.folders);
    } catch (error) {
      console.error('Failed to load folders:', error);
    }
  }, []);

  const loadEmails = useCallback(async (folderName) => {
    setLoading(true);
    try {
      const response = await emailAPI.getEmails(folderName);
      setEmails(response.data.emails);
      setEmailStats({ total: response.data.total, unread: response.data.unread });
      // Cache emails for offline use
      if (response.data.emails.length > 0) {
        cacheEmails(response.data.emails);
      }
    } catch (error) {
      console.error('Failed to load emails:', error);
      // Try offline cache — utilise getCachedEmails via ref pour éviter la stale closure sur isOnline
      const cached = await getCachedEmails(folderName);
      if (cached.length > 0) {
        setEmails(cached);
        setEmailStats({ total: cached.length, unread: cached.filter(e => !e.is_read).length });
        return;
      }
      toast.error('Erreur lors du chargement des emails');
    } finally {
      setLoading(false);
    }
  }, [cacheEmails, getCachedEmails]);

  const loadSignatures = useCallback(async () => {
    try {
      const response = await signaturesAPI.getSignatures();
      setSignatures(response.data.signatures);
    } catch (error) {
      console.error('Failed to load signatures:', error);
    }
  }, []);

  useEffect(() => {
    loadFolders();
    loadSignatures();
  }, [loadFolders, loadSignatures]);

  useEffect(() => {
    const folder = urlFolder || 'INBOX';
    setSelectedFolder(folder);
    loadEmails(folder);
  }, [urlFolder, loadEmails]);

  const handleFolderSelect = (folder) => {
    const imapName = folder.imap_name || folder.name;
    setSelectedFolder(imapName);
    setSelectedEmail(null);
    navigate(`/email/${encodeURIComponent(imapName)}`);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await emailAPI.syncEmails(selectedFolder);
      await loadEmails(selectedFolder);
      toast.success('Emails synchronisés');
    } catch (error) {
      toast.error('Erreur de synchronisation: ' + (error.response?.data?.detail || 'Réessayez'));
    } finally {
      setSyncing(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      loadEmails(selectedFolder);
      return;
    }
    setLoading(true);
    try {
      const response = await emailAPI.searchEmails(searchQuery);
      setEmails(response.data.emails);
      setSearchResults({ query: searchQuery, total: response.data.emails.length });
    } catch (error) {
      toast.error('Erreur de recherche');
    } finally {
      setLoading(false);
    }
  };

  const handleAdvancedSearchResults = (results, total) => {
    setEmails(results);
    setSearchResults({ query: 'Recherche avancée', total });
    setAdvancedSearchOpen(false);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
    loadEmails(selectedFolder);
  };

  const handleMarkRead = async (emailId, isRead) => {
    try {
      await emailAPI.markAsRead(emailId, isRead);
      setEmails(emails.map(e => e.id === emailId ? { ...e, is_read: isRead } : e));
      if (selectedEmail?.id === emailId) {
        setSelectedEmail({ ...selectedEmail, is_read: isRead });
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleToggleFlag = async (emailId, isFlagged) => {
    try {
      await emailAPI.toggleFlag(emailId, isFlagged);
      setEmails(emails.map(e => e.id === emailId ? { ...e, is_flagged: isFlagged } : e));
      if (selectedEmail?.id === emailId) {
        setSelectedEmail({ ...selectedEmail, is_flagged: isFlagged });
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (emailId) => {
    try {
      await emailAPI.deleteEmail(emailId);
      setEmails(emails.filter(e => e.id !== emailId));
      if (selectedEmail?.id === emailId) {
        setSelectedEmail(null);
      }
      toast.success('Email supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleMoveEmail = async (emailId, targetFolder) => {
    try {
      await emailAPI.moveEmail(emailId, targetFolder);
      setEmails(emails.filter(e => e.id !== emailId));
      if (selectedEmail?.id === emailId) {
        setSelectedEmail(null);
      }
      toast.success(`Email déplacé vers ${targetFolder}`);
    } catch (error) {
      toast.error('Erreur lors du déplacement');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Hier';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('fr-FR', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    }
  };

  const getFolderIcon = (type) => {
    return folderIcons[type] || Folder;
  };

  return (
    <div className="h-full flex">
      {/* Folders Panel */}
      <div className="w-60 border-r border-border bg-card flex flex-col">
        <div className="p-4">
          <Button 
            className="w-full gap-2 btn-hover" 
            onClick={() => setComposeOpen(true)}
            data-testid="compose-email-btn"
          >
            <Pencil className="w-4 h-4" />
            Nouveau message
          </Button>
        </div>

        <ScrollArea className="flex-1">
          <div className="px-3 py-2">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 px-2">
              Dossiers
            </div>
            {folders.map((folder) => {
              const Icon = getFolderIcon(folder.type);
              const imapName = folder.imap_name || folder.name;
              return (
                <Button
                  key={folder.id}
                  variant="ghost"
                  className={cn(
                    "w-full justify-start gap-3 h-10 mb-1 sidebar-item transition-all",
                    selectedFolder === imapName && "bg-primary/10 text-primary",
                    dragOverFolder === imapName && "bg-primary/20 ring-2 ring-primary ring-inset"
                  )}
                  onClick={() => handleFolderSelect(folder)}
                  onDragOver={(e) => handleDragOver(e, imapName)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, imapName)}
                  data-testid={`folder-${folder.type}`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1 text-left truncate">{folder.name}</span>
                  {folder.unread_count > 0 && (
                    <Badge variant="secondary" className="ml-auto">
                      {folder.unread_count}
                    </Badge>
                  )}
                </Button>
              );
            })}
          </div>
        </ScrollArea>
      </div>

      {/* Email List Panel */}
      <div className="w-96 border-r border-border flex flex-col bg-background">
        {/* Search and actions */}
        <div className="p-3 border-b border-border space-y-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher..."
                className="pl-9 pr-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                data-testid="email-search-input"
              />
              {searchResults && (
                <button
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={clearSearch}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setAdvancedSearchOpen(true)}
              title="Recherche avancée"
              data-testid="advanced-search-btn"
            >
              <Search className="w-4 h-4" />
            </Button>
            <Button 
              variant="outline" 
              size="icon"
              onClick={handleSync}
              disabled={syncing}
              data-testid="sync-emails-btn"
            >
              <RefreshCw className={cn("w-4 h-4", syncing && "animate-spin")} />
            </Button>
          </div>
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            {searchResults ? (
              <span className="text-primary">{searchResults.total} résultat(s) pour "{searchResults.query}"</span>
            ) : (
              <>
                <span>{emailStats.total} messages</span>
                <span>{emailStats.unread} non lu(s)</span>
              </>
            )}
          </div>
        </div>

        {/* Email list */}
        <ScrollArea className="flex-1">
          {loading ? (
            <div className="p-3 space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="p-3 space-y-2">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-3 w-full" />
                </div>
              ))}
            </div>
          ) : emails.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Mail className="w-12 h-12 mb-3 opacity-50" />
              <p>Aucun email</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {emails.map((email) => (
                <div
                  key={email.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, email.id)}
                  className={cn(
                    "p-3 cursor-grab active:cursor-grabbing email-item group",
                    selectedEmail?.id === email.id && "bg-primary/5",
                    !email.is_read && "bg-muted/50"
                  )}
                  onClick={() => {
                    setSelectedEmail(email);
                    if (!email.is_read) {
                      handleMarkRead(email.id, true);
                    }
                  }}
                  data-testid={`email-item-${email.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 pt-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-6 h-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFlag(email.id, !email.is_flagged);
                        }}
                      >
                        <Star className={cn(
                          "w-4 h-4",
                          email.is_flagged && "fill-yellow-400 text-yellow-400"
                        )} />
                      </Button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className={cn(
                          "text-sm truncate",
                          !email.is_read && "font-semibold"
                        )}>
                          {email.from_address}
                        </span>
                        <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                          {formatDate(email.date)}
                        </span>
                      </div>
                      <p className={cn(
                        "text-sm truncate mb-1",
                        !email.is_read ? "font-medium" : "text-muted-foreground"
                      )}>
                        {email.subject || '(Sans objet)'}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {email.body_text?.slice(0, 100) || 'Aucun aperçu disponible'}
                      </p>
                    </div>
                    <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="w-7 h-7">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleMarkRead(email.id, !email.is_read)}>
                            {email.is_read ? <Mail className="w-4 h-4 mr-2" /> : <MailOpen className="w-4 h-4 mr-2" />}
                            {email.is_read ? 'Marquer non lu' : 'Marquer lu'}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleMoveEmail(email.id, 'Archive')}>
                            <Archive className="w-4 h-4 mr-2" />
                            Archiver
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(email.id)}
                            className="text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  {email.has_attachments && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                      <Paperclip className="w-3 h-3" />
                      <span>Pièce jointe</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Email Detail Panel */}
      <div className="flex-1 flex flex-col bg-background">
        {selectedEmail ? (
          <EmailDetail
            email={selectedEmail}
            onClose={() => setSelectedEmail(null)}
            onReply={() => setComposeOpen(true)}
            onDelete={() => handleDelete(selectedEmail.id)}
            onToggleFlag={() => handleToggleFlag(selectedEmail.id, !selectedEmail.is_flagged)}
          />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
            <Mail className="w-16 h-16 mb-4 opacity-30" />
            <p className="text-lg">Sélectionnez un email</p>
            <p className="text-sm">pour afficher son contenu</p>
          </div>
        )}
      </div>

      {/* Compose Email Dialog */}
      <ComposeEmail
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        signatures={signatures}
        replyTo={selectedEmail}
        onSent={() => {
          loadEmails(selectedFolder);
          loadFolders();
        }}
      />

      {/* Advanced Search */}
      <AdvancedSearch
        open={advancedSearchOpen}
        onClose={() => setAdvancedSearchOpen(false)}
        onResults={handleAdvancedSearchResults}
      />
    </div>
  );
}
